import React, { useEffect, useState, useRef } from "react";
import { 
  Container, Box, Paper, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Button, TextField, 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  Typography, IconButton, Avatar, Chip, Stack,
  CircularProgress, Alert, Snackbar
} from "@mui/material";
import { Edit, Delete, Add, Close } from "@mui/icons-material";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const slugify = (text = "") =>
  text
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");

const AdminBlog = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({ 
    title: "", 
    slug: "",
    description: "", 
    category: "", 
    image: null 
  });
  const [editForm, setEditForm] = useState({ 
    id: "", 
    title: "", 
    slug: "",
    description: "", 
    category: "", 
    image: null 
  });
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const quillRef = useRef(null);
  const editQuillRef = useRef(null);

  // Quill modules configuration
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'font': [] }],
      [{ 'size': [] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }], 
      [{ 'script': 'sub' }, { 'script': 'super' }], 
      [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'align': [] }],
      [{ 'direction': 'rtl' }],
      ['blockquote', 'code-block'],
      ['link', 'image', 'video'],
      ['clean']
    ],
  };
  
  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script', 'list', 'bullet', 'indent',
    'align', 'direction',
    'blockquote', 'code-block',
    'link', 'image', 'video'
  ];

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/blogs`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setBlogs(data.blogs || data);
    } catch (error) {
      setError(`Failed to fetch blogs: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => {
      const updated = { ...prev, [name]: value };
      if (name === "title" && !prev.slug) {
        updated.slug = slugify(value);
      }
      return updated;
    });
  };

  const handleSlugChange = (e) => {
    const value = slugify(e.target.value);
    setForm(prev => ({ ...prev, slug: value }));
  };

  const handleDescriptionChange = (value) => {
    setForm({ ...form, description: value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.match('image.*')) {
        setError('Only image files are allowed');
        return;
      }
      if (file.size > 20 * 1024 * 1024) { // 20MB
        setError('Image size must be less than 20MB');
        return;
      }
      setSelectedImage(URL.createObjectURL(file));
      setForm({ ...form, image: file });
    }
  };

  const handleRemoveImage = () => {
    setForm({ ...form, image: null });
    setSelectedImage(null);
  };

  const validateForm = () => {
    if (!form.title.trim()) {
      setError('Title is required');
      return false;
    }
    if (!form.description.trim()) {
      setError('Description is required');
      return false;
    }
    if (!form.slug.trim()) {
      setError('Slug is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('title', form.title);
      formData.append('description', form.description);
      formData.append('category', form.category);
      formData.append('slug', form.slug);
      if (form.image) {
        formData.append('image', form.image);
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/blogs`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create blog');
      }

      const data = await response.json();
      setSuccess('Blog created successfully');
      fetchBlogs();
      setForm({ title: "", slug: "", description: "", category: "General", image: null });
      setSelectedImage(null);
      setOpenCreateDialog(false);
    } catch (error) {
      setError(error.message || 'Error creating blog');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this blog post?")) {
      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/blogs/${id}`, { 
          method: "DELETE" 
        });
        
        if (!response.ok) {
          throw new Error('Failed to delete blog');
        }
        
        setSuccess('Blog deleted successfully');
        fetchBlogs();
      } catch (error) {
        setError(error.message || 'Error deleting blog');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditOpen = (blog) => {
    setEditForm({ 
      id: blog._id, 
      title: blog.title, 
      slug: blog.slug || "",
      description: blog.description, 
      category: blog.category || "General", 
      image: blog.image 
    });
    setOpenEditDialog(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => {
      const updated = { ...prev, [name]: value };
      if (name === "title" && !prev.slug) {
        updated.slug = slugify(value);
      }
      return updated;
    });
  };

  const handleEditSlugChange = (e) => {
    const value = slugify(e.target.value);
    setEditForm(prev => ({ ...prev, slug: value }));
  };

  const handleEditDescriptionChange = (value) => {
    setEditForm({ ...editForm, description: value });
  };

  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.match('image.*')) {
        setError('Only image files are allowed');
        return;
      }
      if (file.size > 20 * 1024 * 1024) { // 20MB
        setError('Image size must be less than 20MB');
        return;
      }
      setEditForm({ ...editForm, image: file });
    }
  };

  const validateEditForm = () => {
    if (!editForm.title.trim()) {
      setError('Title is required');
      return false;
    }
    if (!editForm.description.trim()) {
      setError('Description is required');
      return false;
    }
    if (!editForm.slug.trim()) {
      setError('Slug is required');
      return false;
    }
    return true;
  };

  const handleUpdate = async () => {
    if (!validateEditForm()) return;
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('title', editForm.title);
      formData.append('description', editForm.description);
      formData.append('category', editForm.category);
      formData.append('slug', editForm.slug);
      if (editForm.image instanceof File) {
        formData.append('image', editForm.image);
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/blogs/${editForm.id}`, {
        method: "PUT",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update blog');
      }

      setSuccess('Blog updated successfully');
      fetchBlogs();
      setOpenEditDialog(false);
    } catch (error) {
      setError(error.message || 'Error updating blog');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setError(null);
    setSuccess(null);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Snackbars for error and success messages */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: "bold", color: "primary.main" }}>
          Blog Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setOpenCreateDialog(true)}
          sx={{ 
            backgroundColor: "primary.main",
            color: "white",
            '&:hover': { backgroundColor: "primary.dark" }
          }}
        >
          New Blog Post
        </Button>
      </Box>

      {/* Blog Table */}
      <Paper elevation={3} sx={{ p: 2 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "primary.main" }}>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>Title</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>Description</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>Slug</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>Category</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>Image</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {blogs.map((blog) => (
                  <TableRow key={blog._id} hover>
                    <TableCell sx={{ fontWeight: "medium" }}>{blog.title}</TableCell>
                    <TableCell>
                      <Box 
                        sx={{ 
                          maxHeight: 100, 
                          maxWidth: 100,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical'
                        }}
                        dangerouslySetInnerHTML={{ __html: blog.description }} 
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{blog.slug || '—'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={blog.category || "General"} 
                        color="primary" 
                        variant="outlined" 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      {blog.image && (
                        <Avatar
                          variant="rounded"
                          src={`${process.env.REACT_APP_API_BASE_URL}/${blog.image}`}
                          alt={blog.title}
                          sx={{ width: 80, height: 60 }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <IconButton 
                          color="primary" 
                          onClick={() => handleEditOpen(blog)}
                          sx={{ '&:hover': { backgroundColor: 'primary.light' } }}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton 
                          color="error" 
                          onClick={() => handleDelete(blog._id)}
                          sx={{ '&:hover': { backgroundColor: 'error.light' } }}
                        >
                          <Delete />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Create Blog Dialog */}
      <Dialog 
        open={openCreateDialog} 
        onClose={() => !isSubmitting && setOpenCreateDialog(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Create New Blog Post</Typography>
            <IconButton 
              onClick={() => !isSubmitting && setOpenCreateDialog(false)}
              disabled={isSubmitting}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <TextField 
            label="Title" 
            name="title" 
            value={form.title} 
            onChange={handleChange} 
            fullWidth 
            sx={{ mb: 3 }}
            required
            error={!form.title.trim()}
            helperText={!form.title.trim() ? 'Title is required' : ''}
          />
          
          <TextField 
            label="Slug (used in blog URL)" 
            name="slug" 
            value={form.slug} 
            onChange={handleSlugChange} 
            fullWidth 
            sx={{ mb: 3 }}
            required
            error={!form.slug.trim()}
            helperText={!form.slug.trim() ? 'Slug is required' : 'Auto-generated from title. You can customize it.'}
          />
          
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Description *</Typography>
          <ReactQuill 
            ref={quillRef}
            theme="snow" 
            value={form.description} 
            onChange={handleDescriptionChange}
            modules={modules}
            formats={formats}
            style={{ height: '300px', marginBottom: '60px' }}
          />
          {!form.description.trim() && (
            <Typography color="error" variant="caption">Description is required</Typography>
          )}
          
          <TextField 
            label="Category" 
            name="category" 
            value={form.category} 
            onChange={handleChange} 
            fullWidth 
            sx={{ mb: 3 }}
          />
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Featured Image</Typography>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="blog-image-upload"
              type="file"
              onChange={handleImageChange}
              disabled={isSubmitting}
            />
            <label htmlFor="blog-image-upload">
              <Button variant="outlined" component="span" disabled={isSubmitting}>
                Upload Image
              </Button>
            </label>
            {selectedImage && (
              <Box sx={{ mt: 2, position: 'relative', display: 'inline-block' }}>
                <img
                  src={selectedImage}
                  alt="Preview"
                  style={{ maxHeight: 150, maxWidth: '100%', borderRadius: 4 }}
                />
                <IconButton
                  size="small"
                  onClick={handleRemoveImage}
                  disabled={isSubmitting}
                  sx={{ 
                    position: 'absolute', 
                    top: 0, 
                    right: 0, 
                    backgroundColor: 'rgba(0,0,0,0.5)', 
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' }
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenCreateDialog(false)} 
            color="error"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            color="primary" 
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Create Post'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Blog Dialog */}
      <Dialog 
        open={openEditDialog} 
        onClose={() => !isSubmitting && setOpenEditDialog(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Edit Blog Post</Typography>
            <IconButton 
              onClick={() => !isSubmitting && setOpenEditDialog(false)}
              disabled={isSubmitting}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <TextField 
            label="Title" 
            name="title" 
            value={editForm.title} 
            onChange={handleEditChange} 
            fullWidth 
            sx={{ mb: 3 }}
            required
            error={!editForm.title.trim()}
            helperText={!editForm.title.trim() ? 'Title is required' : ''}
          />
          
          <TextField 
            label="Slug (used in blog URL)" 
            name="slug" 
            value={editForm.slug} 
            onChange={handleEditSlugChange} 
            fullWidth 
            sx={{ mb: 3 }}
            required
            error={!editForm.slug.trim()}
            helperText={!editForm.slug.trim() ? 'Slug is required' : 'Auto-generated from title. You can customize it.'}
          />
          
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Description *</Typography>
          <ReactQuill 
            ref={editQuillRef}
            theme="snow" 
            value={editForm.description} 
            onChange={handleEditDescriptionChange}
            modules={modules}
            formats={formats}
            style={{ height: '300px', marginBottom: '60px' }}
          />
          {!editForm.description.trim() && (
            <Typography color="error" variant="caption">Description is required</Typography>
          )}
          
          <TextField 
            label="Category" 
            name="category" 
            value={editForm.category} 
            onChange={handleEditChange} 
            fullWidth 
            sx={{ mb: 3 }}
          />
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Featured Image</Typography>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="edit-blog-image-upload"
              type="file"
              onChange={handleEditImageChange}
              disabled={isSubmitting}
            />
            <label htmlFor="edit-blog-image-upload">
              <Button variant="outlined" component="span" disabled={isSubmitting}>
                Change Image
              </Button>
            </label>
            {editForm.image && !(editForm.image instanceof File) && (
              <Box sx={{ mt: 2 }}>
                <img
                  src={`${process.env.REACT_APP_API_BASE_URL}/${editForm.image}`}
                  alt="Current"
                  style={{ maxHeight: 150, maxWidth: '100%', borderRadius: 4 }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenEditDialog(false)} 
            color="error"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpdate} 
            color="primary" 
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Update Post'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
export default AdminBlog;